import React, { useState, useEffect, useRef } from 'react';
import { Modal, View, Text, TextInput, Pressable, StyleSheet, ScrollView, Alert, Platform } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { GOOGLE_MAPS_CONFIG, GOOGLE_MAPS_API_KEY } from '../../../constants/GoogleMapsConfig';
import AddressAutocomplete from '../../../components/general/AddressAutocomplete';
import { useLoadScript } from '@react-google-maps/api';

interface Address {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface EditAddressModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (address: Address) => void;
  currentAddress: Address;
}

const EditAddressModal: React.FC<EditAddressModalProps> = ({
  visible,
  onClose,
  onSave,
  currentAddress,
}) => {
  const [address, setAddress] = useState<Address>(currentAddress);
  const [searchQuery, setSearchQuery] = useState('');
  const autocompleteRef = useRef<any>(null);
  const { isLoaded } = useLoadScript(GOOGLE_MAPS_CONFIG);

  const placesService = useRef<any>(null);

  useEffect(() => {
    if (isLoaded && (window as any).google && !placesService.current) {
      // We need an element for PlacesService but it can be a dummy one or none if using a specific constructor
      // Actually, on web you can just pass a div or even document.createElement('div')
      const dummy = document.createElement('div');
      placesService.current = new (window as any).google.maps.places.PlacesService(dummy);
    }
  }, [isLoaded]);

  const handlePlaceSelectWithDetails = (placeId: string, description: string) => {
    if (!placesService.current) {
      console.warn('PlacesService not ready');
      // Fallback: use description if available
      return;
    }

    placesService.current.getDetails(
      {
        placeId: placeId,
        fields: ['address_components', 'formatted_address', 'geometry']
      },
      (place: any, status: any) => {
        if (status === (window as any).google.maps.places.PlacesServiceStatus.OK) {
          handlePlaceSelect(place);
        } else {
          console.error('Failed to get place details:', status);
          // Fallback: at least set the search query
          setSearchQuery(description);
        }
      }
    );
  };

  const handlePlaceSelect = (place: any) => {
    const components = place.address_components || [];
    let streetNumber = '', route = '', city = '', state = '', zipCode = '', country = '';

    components.forEach((c: any) => {
      const types = c.types;
      if (types.includes('street_number')) streetNumber = c.long_name;
      if (types.includes('route')) route = c.long_name;
      if (types.includes('locality')) city = c.long_name;
      if (types.includes('administrative_area_level_1')) state = c.short_name;
      if (types.includes('postal_code')) zipCode = c.long_name;
      if (types.includes('country')) country = c.long_name;
    });

    setAddress({
      street: `${streetNumber} ${route}`.trim(),
      city,
      state,
      zipCode,
      country,
    });
    setSearchQuery(place.formatted_address || '');
  };

  const handleSave = () => {
    if (!address.street || !address.city || !address.state) {
      Alert.alert('Missing Information', 'Please fill in at least street, city, and state.');
      return;
    }
    onSave(address);
    onClose();
  };

  const handleCancel = () => {
    setAddress(currentAddress);
    onClose();
  };

  const updateAddressField = (field: keyof Address, value: string) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={handleCancel}
    >

      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerTitle}>Edit Address</Text>
            <Pressable onPress={handleCancel} style={styles.closeButton}>
              <MaterialCommunityIcons name="close" size={24} color="#666" />
            </Pressable>
          </View>

          {/* Content */}
          <ScrollView
            style={styles.content}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.scrollContent}
          >
            <View style={[styles.section, { zIndex: 100 }]}>
              <Text style={styles.sectionTitle}>Search Address</Text>
              <Text style={styles.sectionSubtitle}>
                Start typing to search for your address, or fill in manually below.
              </Text>

              {Platform.OS === 'web' ? (
                <AddressAutocomplete
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  onSelectPlace={handlePlaceSelectWithDetails}
                  placeholder="Search for your address..."
                  containerStyle={{ zIndex: 1000 }}
                />
              ) : (
                <TextInput
                  style={styles.input}
                  placeholder="Street Address"
                  value={address.street}
                  onChangeText={(text) => updateAddressField('street', text)}
                />
              )}
            </View>

            {/* Manual Address Fields */}
            <View style={[styles.section, { zIndex: 1 }]}>
              <Text style={styles.sectionTitle}>Address Details</Text>

              <View style={styles.fieldGroup}>
                <Text style={styles.fieldLabel}>Street Address</Text>
                <TextInput
                  style={styles.input}
                  value={address.street}
                  onChangeText={(text) => updateAddressField('street', text)}
                  placeholder="123 Main Street"
                  placeholderTextColor="#999"
                />
              </View>

              <View style={styles.fieldRow}>
                <View style={[styles.fieldGroup, styles.flexField]}>
                  <Text style={styles.fieldLabel}>City</Text>
                  <TextInput
                    style={styles.input}
                    value={address.city}
                    onChangeText={(text) => updateAddressField('city', text)}
                    placeholder="City"
                    placeholderTextColor="#999"
                  />
                </View>
                <View style={[styles.fieldGroup, styles.flexField]}>
                  <Text style={styles.fieldLabel}>State</Text>
                  <TextInput
                    style={styles.input}
                    value={address.state}
                    onChangeText={(text) => updateAddressField('state', text)}
                    placeholder="State"
                    placeholderTextColor="#999"
                  />
                </View>
              </View>

              <View style={styles.fieldRow}>
                <View style={[styles.fieldGroup, styles.flexField]}>
                  <Text style={styles.fieldLabel}>ZIP Code</Text>
                  <TextInput
                    style={styles.input}
                    value={address.zipCode}
                    onChangeText={(text) => updateAddressField('zipCode', text)}
                    placeholder="12345"
                    placeholderTextColor="#999"
                    keyboardType="numeric"
                  />
                </View>
                <View style={[styles.fieldGroup, styles.flexField]}>
                  <Text style={styles.fieldLabel}>Country</Text>
                  <TextInput
                    style={styles.input}
                    value={address.country}
                    onChangeText={(text) => updateAddressField('country', text)}
                    placeholder="Country"
                    placeholderTextColor="#999"
                  />
                </View>
              </View>
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <Pressable style={styles.cancelButton} onPress={handleCancel}>
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </Pressable>
            <Pressable style={styles.saveButton} onPress={handleSave}>
              <Text style={styles.saveButtonText}>Save Address</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    zIndex: 1000,
  },
  modalContainer: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '100%',
    maxWidth: 600,
    maxHeight: '90%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 10,
    zIndex: 1001,
    overflow: 'visible',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#222',
  },
  closeButton: {
    padding: 4,
  },
  content: {
    flex: 1,
    padding: 20,
    overflow: 'visible',
  },
  scrollContent: {
    overflow: 'visible',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#222',
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
    lineHeight: 20,
  },
  fieldGroup: {
    marginBottom: 16,
  },
  fieldRow: {
    flexDirection: 'row',
    gap: 12,
  },
  flexField: {
    flex: 1,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: '#333',
    backgroundColor: '#F8F8F8',
  },
  footer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#666',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#3C8248',
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
});

export default EditAddressModal;
